import { api, ApiError } from "../lib/api";
import { getToken, getUser } from "../lib/auth";
import { ensureAppTimezone, fmtDateTime, fmtSchedule } from "../lib/datetime";
import { registerPageCleanup } from "../lib/router";
import { renderHeader } from "../components/header";
import {
  isRateLimitInfo,
  rateLimitOneLiner,
  showRateLimitModal,
  type RateLimitInfo,
} from "../components/rate_limit_modal";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A run and the counters explaining what it did.
 *
 * Every count is nullable: rows written before the collector kept them read as
 * unknown, and rendering a confident 0 for them would be inventing a fact.
 */
interface RunSummary {
  runId: string;
  startedAt: string;
  completedAt: string | null;
  status: string;
  signalsFound: number | null;
  errorsCount: number | null;
  articlesSeen: number | null;
  skippedRelevance: number | null;
  skippedParse: number | null;
  skippedLlm: number | null;
}

type RunState = "idle" | "running" | "stopping";

interface CollectStatus {
  status: RunState;
  runId?: string | null;
  startedAt?: string | null;
  stopRequested: boolean;
  lastRun?: RunSummary | null;
  note?: string | null;
}

interface RunResponse {
  ok: boolean;
  runId: string;
}

/** Mirrors backend/schemas/collect.py MAX_URLS. */
const MAX_URLS = 10;

interface UrlResult {
  url: string;
  /** Every one of these used to arrive as "failed" with a stringified
   *  exception in the reason — including the two that are not failures:
   *  a duplicate is the app working, and a deferred URL was never attempted. */
  status:
    | "success"
    | "duplicate"
    | "fetch_failed"
    | "rate_limited"
    | "analysis_failed"
    | "failed"
    | "stopped"
    | "deferred";
  signal_id?: string;
  reason?: string;
}

interface UrlSubmitResponse {
  results: UrlResult[];
  /** Set when at least one URL was refused by the provider. The batch still
   *  answers 200 — the successes are committed — so the modal is raised from
   *  here rather than by api()'s 429 path. */
  rateLimit?: RateLimitInfo | null;
  /** The batch ended before every URL was attempted. */
  truncated?: boolean;
}

interface ScanSchedule {
  enabled: boolean;
  days: string[];
  hour: number;
  minute: number;
  timezone: string;
  maxArticlesPerRun: number;
  /** The morning brief runs on the same days, in the same zone, at its own time. */
  briefEnabled: boolean;
  briefHour: number;
  briefMinute: number;
  allDays: string[];
  nextRunAt: string | null;
  nextBriefAt: string | null;
  lastRun: RunSummary | null;
  lastBrief: LastBrief | null;
  /** Today's generation attempts per cadence, as the retry sweep sees them. */
  briefAttempts?: { daily: BriefAttempts | null; weekly: BriefAttempts | null };
  /** A month of briefs priced on Claude, from measured tokens. Null until a
   *  brief has recorded its usage. */
  briefCost?: { perMonthUsd: Record<string, number>; samples: Record<string, number> } | null;
  activeRun: { runId: string; startedAt: string; stopRequested: boolean } | null;
}

/** One cadence's attempt log (backend/brief_retry.py). A failed brief is
 *  retried by the delivery sweep, so "failed" at 09:16 may be "sent" at 09:50 —
 *  this row is what shows the difference. */
interface BriefAttempts {
  day: string;
  maxAttempts: number;
  attempts: { at: string; outcome: "ok" | "transient" | "permanent" | "daily_quota"; detail: string }[];
}

/** What the last morning-brief job did. A skipped brief is a decision, not a
 *  failure, and the panel has to be able to tell them apart. */
interface LastBrief {
  /** `generating` is not a failure: the brief outlived the two minutes the
   *  delivering job waits and is still being written. The delivery sweep
   *  replaces it with `sent` when the brief lands. */
  status: "sent" | "skipped_no_signals" | "failed" | "generating";
  date: string;
  at: string;
  detail: string | null;
}

// ---------------------------------------------------------------------------
// SSE log streaming (auth-aware via fetch, not EventSource)
// ---------------------------------------------------------------------------
//
// Module-level rather than page-local because the cursor has to survive a
// reconnect, and registerPageCleanup has to be able to reach it on navigation.

let _logAbortController: AbortController | null = null;
let _reconnectTimer: number | null = null;
/** Lines consumed from the current run — sent back as the cursor so a
 *  reconnect resumes instead of replaying the run from the top. */
let _logLineCount = 0;
let _currentRunId: string | null = null;
let _streamAttempts = 0;
/** Set when the page tears the stream down deliberately, so the error path can
 *  tell a navigation from a dropped connection and not reconnect into a dead
 *  page. */
let _streamRetired = false;

/** Give up reconnecting after this many consecutive failures. */
const _MAX_STREAM_ATTEMPTS = 5;

/** The last state the server reported. The page branches on this rather than on
 *  the button label it happens to have painted. */
let _runState: RunState = "idle";
let _statusPoll: number | null = null;
/** Set once the stream has given up, so the status poll does not quietly
 *  reattach every five seconds and print the same failure over and over. */
let _streamGaveUp = false;

function stopStatusPoll(): void {
  if (_statusPoll !== null) {
    window.clearTimeout(_statusPoll);
    _statusPoll = null;
  }
}

/** True while a stream is connected or waiting to retry. */
function isStreamLive(): boolean {
  return _logAbortController !== null || _reconnectTimer !== null;
}

/** Marks a log line carrying a rate-limit envelope rather than prose.
 *  Written by backend/collector.py; the envelope itself is JSON. */
const RATE_LIMIT_PREFIX = "[rate-limit-event] ";

/** Raise the modal for a `[rate-limit-event]` line, and return what the log
 *  should show in its place — a sentence, not the JSON that carried it. */
function readRateLimitEvent(line: string): string {
  try {
    const info = JSON.parse(line.slice(RATE_LIMIT_PREFIX.length));
    if (isRateLimitInfo(info)) {
      showRateLimitModal(info);
      return `[rate-limit] ${rateLimitOneLiner(info)}`;
    }
  } catch {
    // Unparseable: fall through and print it as it arrived. A malformed event
    // is still evidence of something, and swallowing the line loses it.
  }
  return line;
}

interface StreamHandlers {
  onLine: (line: string) => void;
  /** The run genuinely ended — [done] or [no active run]. */
  onEnd: () => void;
  /** A different run is on the wire; the viewer should start clean. */
  onRunSwitch: (runId: string) => void;
  /** Out-of-band message for the log viewer (stream lost, retrying). */
  onNotice: (message: string) => void;
}

async function connectLogStream(h: StreamHandlers): Promise<void> {
  stopLogStream();
  _streamRetired = false;
  _logAbortController = new AbortController();
  const token = getToken();

  const params = new URLSearchParams({ cursor: String(_logLineCount) });
  if (_currentRunId) params.set("run_id", _currentRunId);

  try {
    const response = await fetch(`/api/collect/logs?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token ?? ""}` },
      signal: _logAbortController.signal,
    });
    if (response.status === 429) {
      // The other path that bypasses api(): a streaming fetch, so api()'s 429
      // branch never sees it. Explained here and then treated as any other
      // failed connection — the run itself is unaffected by our reconnecting.
      const detail = await response.json().then((d) => d?.detail).catch(() => null);
      if (isRateLimitInfo(detail)) showRateLimitModal(detail);
    }
    if (!response.ok || !response.body) {
      scheduleReconnect(h);
      return;
    }
    _streamAttempts = 0;
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const lines = buf.split("\n");
      buf = lines.pop() ?? "";
      for (const line of lines) {
        // Anything that is not a `data: ` line is protocol furniture — the
        // keepalive comment arrives here and is meant to be ignored.
        if (!line.startsWith("data: ")) continue;
        const data = line.slice(6);

        if (data.startsWith("[run] ")) {
          const id = data.slice(6).trim();
          if (id !== _currentRunId) {
            _currentRunId = id;
            _logLineCount = 0;
            h.onRunSwitch(id);
          }
          continue;
        }
        if (data === "[done]" || data === "[no active run]") {
          _streamRetired = true;
          h.onEnd();
          return;
        }
        _logLineCount += 1;
        if (data.startsWith(RATE_LIMIT_PREFIX)) {
          // A rate limit hit by a run nobody is holding a request open for —
          // an overnight scan, most of the time. The collector writes the same
          // envelope the 429 body carries, so the modal does not care which
          // arrived. Counted as a line first: the server counts it too, and the
          // cursor is what a reconnect resumes from.
          h.onLine(readRateLimitEvent(data));
          continue;
        }
        h.onLine(data);
      }
    }
  } catch (_e) {
    // Aborted by us, or the connection dropped. Only the second deserves a retry.
  }

  // Reaching here means the stream ended without saying so. Previously this
  // called onDone(), which flipped the button back to "Run Now" mid-run every
  // time a proxy timed the connection out — the run was still going.
  if (!_streamRetired) scheduleReconnect(h);
}

function scheduleReconnect(h: StreamHandlers): void {
  if (_streamRetired) return;
  _streamAttempts += 1;
  if (_streamAttempts > _MAX_STREAM_ATTEMPTS) {
    h.onNotice(
      "[log stream lost] Could not reattach to the log after " +
        `${_MAX_STREAM_ATTEMPTS} attempts — status polling continues, and Run ` +
        "History will show how the run ended."
    );
    return;
  }
  // 1s, 2s, 4s, 8s, 16s.
  const delay = Math.min(16000, 1000 * 2 ** (_streamAttempts - 1));
  _reconnectTimer = window.setTimeout(() => void connectLogStream(h), delay);
}

function stopLogStream(): void {
  _logAbortController?.abort();
  _logAbortController = null;
  if (_reconnectTimer !== null) {
    window.clearTimeout(_reconnectTimer);
    _reconnectTimer = null;
  }
}

/** Tear the stream down for good — navigation, or a run that ended. */
function retireLogStream(): void {
  _streamRetired = true;
  stopLogStream();
}

// ---------------------------------------------------------------------------
// Page renderer
// ---------------------------------------------------------------------------

export function renderSignalManagementPage(root: HTMLElement): void {
  root.appendChild(renderHeader());

  const shell = document.createElement("div");
  shell.className = "page-shell";

  // Scheduling controls unattended spend, and Configuration holds the feed list
  // this server fetches plus the skill text injected into every LLM prompt — so
  // both tabs are admin-only. The server enforces this too (require_admin on
  // /collect/scanning, /collect/config and /collect/skill); hiding the tabs is
  // only to avoid showing controls that would 403.
  const isAdmin = getUser()?.role === "admin";

  shell.innerHTML = `
    <div class="sm-page">
      <div class="sm-header" style="display:flex;align-items:center;justify-content:space-between;gap:16px;">
        <h2 style="margin:0">Signal Management</h2>
        <a href="/wikilinks" style="font-size:13px;">Manage Wikilinks →</a>
      </div>
      <div class="tab-bar" role="tablist">
        <button class="tab-btn active" data-tab="collection" role="tab">Collection</button>
        ${isAdmin ? '<button class="tab-btn" data-tab="config" role="tab">Configuration</button>' : ""}
        <button class="tab-btn" data-tab="urls" role="tab">Submit URLs</button>
        ${isAdmin ? '<button class="tab-btn" data-tab="scanning" role="tab">Automated Scanning</button>' : ""}
      </div>

      <!-- Collection tab -->
      <div class="tab-panel" id="tab-collection">
        <div class="run-controls">
          <div class="status-row">
            <span class="status-badge" id="status-badge">idle</span>
            <span class="muted" id="last-run-info"></span>
            <span class="muted run-note" id="run-note"></span>
          </div>
          <button class="primary" id="run-btn">Run Now</button>
        </div>
        <div class="log-viewer">
          <div class="log-viewer-header">
            <span class="log-label mono">Collection log</span>
            <button class="icon-btn" id="clear-log-btn" title="Clear log">&#x2715;</button>
          </div>
          <pre class="log-output mono" id="log-output">No run yet. Click "Run Now" to start a collection.</pre>
        </div>
        <div class="history-section">
          <h3>Run History</h3>
          <div id="history-container"><p class="muted">Loading…</p></div>
        </div>
      </div>

      <!-- Configuration tab -->
      <div class="tab-panel hidden" id="tab-config">
        <div class="config-editors">
          <div class="config-section">
            <div class="config-section-header">
              <div>
                <h3>config.yaml</h3>
                <p class="muted">RSS feed sources and collection schedule (cron syntax)</p>
              </div>
              <div class="config-actions">
                <span class="save-msg" id="config-save-msg"></span>
                <button class="primary" id="save-config-btn">Save</button>
              </div>
            </div>
            <textarea class="code-editor" id="config-editor" rows="20" spellcheck="false"></textarea>
          </div>
          <div class="config-section">
            <div class="config-section-header">
              <div>
                <h3>Guidance</h3>
                <p class="muted">
                  What the models are told. Each document goes to a different part of the
                  pipeline — the tabs say which.
                </p>
              </div>
              <div class="config-actions">
                <span class="save-msg" id="guidance-save-msg"></span>
                <button class="primary" id="save-guidance-btn">Save</button>
              </div>
            </div>
            <div class="doc-tabs" id="guidance-tabs" role="tablist"></div>
            <p class="muted doc-blurb" id="guidance-blurb"></p>
            <textarea class="code-editor" id="guidance-editor" rows="26" spellcheck="false"></textarea>
            <p class="muted skill-count" id="guidance-count"></p>
            <div class="guidance-budget" id="guidance-budget" hidden></div>
            <details class="gl-panel" id="guidance-load">
              <summary>Load v6 guidance</summary>
              <p class="muted">
                These documents live in the database once you have saved one, so a release
                that rewrites them reaches this install only from here. Nothing is
                overwritten until you tick a document and confirm.
              </p>
              <div class="gl-body" id="gl-body"></div>
            </details>
          </div>
          <details class="config-section legacy-skill" id="legacy-skill">
            <summary>Previous single document (skill.md)</summary>
            <p class="muted">
              Read-only. Guidance used to be one document; it is now four. If you had edited
              this, its full text was carried over into <strong>scope.md</strong> so nothing
              was lost — which means anything belonging in another document is currently in
              two places. Move it across, then this can go.
            </p>
            <textarea class="code-editor" id="skill-editor" rows="16" spellcheck="false" readonly></textarea>
          </details>
        </div>
      </div>

      <!-- Submit URLs tab -->
      <div class="tab-panel hidden" id="tab-urls">
        <div class="urls-section">
          <h3>Analyse specific URLs</h3>
          <p class="muted">
            Paste one URL per line, up to ${MAX_URLS} at a time. Each is fetched, analysed,
            and saved as a signal — the relevance check is skipped, since you chose it.
          </p>
          <textarea
            id="url-input"
            class="url-input"
            rows="8"
            placeholder="https://example.com/article-one&#10;https://example.com/article-two"
            spellcheck="false"
          ></textarea>
          <div class="url-submit-row">
            <button class="primary" id="submit-urls-btn">Analyse URLs</button>
            <span class="muted" id="url-submit-status"></span>
          </div>
          <div id="url-results"></div>
        </div>
      </div>

      ${isAdmin ? `
      <!-- Automated Scanning tab (admin only) -->
      <div class="tab-panel hidden" id="tab-scanning">
        <div class="scan-section">
          <h3>Automated Scanning</h3>
          <p class="muted">
            Runs the collection pipeline on a schedule. Manual “Run Now” always works,
            regardless of these settings.
          </p>
          <div id="scan-body"><p class="muted">Loading…</p></div>
        </div>
      </div>` : ""}
    </div>
  `;

  root.appendChild(shell);

  // -------------------------------------------------------------------------
  // Tab switching
  // -------------------------------------------------------------------------

  const tabBtns = shell.querySelectorAll<HTMLButtonElement>(".tab-btn");
  const tabPanels = shell.querySelectorAll<HTMLDivElement>(".tab-panel");

  function switchTab(tabId: string): void {
    tabBtns.forEach((btn) => {
      btn.classList.toggle("active", btn.dataset.tab === tabId);
    });
    tabPanels.forEach((panel) => {
      panel.classList.toggle("hidden", panel.id !== `tab-${tabId}`);
    });
    if (tabId === "config") loadConfigEditors();
    if (tabId === "scanning") void loadScanning();
  }

  tabBtns.forEach((btn) => {
    btn.addEventListener("click", () => switchTab(btn.dataset.tab!));
  });

  // -------------------------------------------------------------------------
  // Collection tab
  // -------------------------------------------------------------------------

  const statusBadge = shell.querySelector<HTMLSpanElement>("#status-badge")!;
  const lastRunInfo = shell.querySelector<HTMLSpanElement>("#last-run-info")!;
  const runNote = shell.querySelector<HTMLSpanElement>("#run-note")!;
  const runBtn = shell.querySelector<HTMLButtonElement>("#run-btn")!;
  const logOutput = shell.querySelector<HTMLPreElement>("#log-output")!;
  const clearLogBtn = shell.querySelector<HTMLButtonElement>("#clear-log-btn")!;
  const historyContainer = shell.querySelector<HTMLDivElement>("#history-container")!;

  const LOG_PLACEHOLDER = 'No run yet. Click "Run Now" to start a collection.';

  // A fresh page owns the run state; anything left by a previous render is stale.
  _runState = "idle";
  stopStatusPoll();

  /** Render one server-reported state. Three branches, not a boolean: `stopping`
   *  is a state the server can be in for minutes, and painting it as `running`
   *  is what made the Stop button look like it had done nothing. */
  function applyState(status: CollectStatus): void {
    _runState = status.status;

    statusBadge.textContent = status.status;
    statusBadge.className = `status-badge ${status.status}`;

    if (status.status === "stopping") {
      runBtn.textContent = "Stopping…";
      runBtn.classList.remove("primary");
      runBtn.classList.add("destructive-btn");
      runBtn.disabled = true;
    } else if (status.status === "running") {
      runBtn.textContent = "Stop";
      runBtn.classList.remove("primary");
      runBtn.classList.add("destructive-btn");
      runBtn.disabled = false;
    } else {
      runBtn.textContent = "Run Now";
      runBtn.classList.remove("destructive-btn");
      runBtn.classList.add("primary");
      runBtn.disabled = false;
    }

    runNote.textContent = status.note ?? "";

    // Only overwrite when the server has something to say, so an optimistic
    // local update does not blank the previous run summary.
    if (status.lastRun) lastRunInfo.textContent = fmtLastRun(status.lastRun);

    // A run started by the scheduler, or by another tab, gets a log stream here
    // without anyone reloading the page.
    if (status.status !== "idle" && !isStreamLive() && !_streamGaveUp) {
      if (status.runId && status.runId !== _currentRunId) {
        _currentRunId = status.runId;
        _logLineCount = 0;
      }
      attachStream();
    }
  }

  function attachStream(): void {
    void connectLogStream({
      onLine: appendLog,
      onEnd: () => {
        void loadStatus();
        void loadHistory();
      },
      onRunSwitch: () => {
        // A different run is on the wire — showing its lines below the previous
        // run would read as one long run.
        logOutput.textContent = "";
      },
      onNotice: (message) => {
        _streamGaveUp = true;
        appendLog(message);
      },
    });
  }

  function appendLog(line: string): void {
    if (logOutput.textContent === LOG_PLACEHOLDER) logOutput.textContent = "";
    logOutput.textContent += line + "\n";
    logOutput.scrollTop = logOutput.scrollHeight;
  }

  clearLogBtn.addEventListener("click", () => {
    logOutput.textContent = "";
  });

  async function loadStatus(): Promise<void> {
    try {
      applyState(await api<CollectStatus>("/collect/status"));
    } catch (_e) {
      // A failed poll says nothing about the run; leave the last known state up.
    } finally {
      scheduleStatusPoll();
    }
  }

  /** Poll faster while something is happening. Status used to be fetched exactly
   *  once, at page load, so a scheduled run beginning a minute later was
   *  invisible until a reload. */
  function scheduleStatusPoll(): void {
    stopStatusPoll();
    _statusPoll = window.setTimeout(
      () => void loadStatus(),
      _runState === "idle" ? 15000 : 5000
    );
  }

  // The router fires this before it blows away the page. First caller in the
  // codebase: without it the poll and the log stream outlive the DOM they were
  // written for, and keep fetching for the rest of the session.
  registerPageCleanup(() => {
    stopStatusPoll();
    retireLogStream();
  });

  async function loadHistory(): Promise<void> {
    try {
      await ensureAppTimezone();
      const rows = await api<RunSummary[]>("/collect/history");
      if (rows.length === 0) {
        historyContainer.innerHTML = "<p class='muted'>No runs yet.</p>";
        return;
      }
      historyContainer.innerHTML = `
        <table class="history-table">
          <thead>
            <tr>
              <th>Started</th>
              <th>Duration</th>
              <th>Status</th>
              <th>Seen</th>
              <th>Signals</th>
              <th>Skipped</th>
              <th>Errors</th>
            </tr>
          </thead>
          <tbody>
            ${rows
              .map((r) => {
                const start = new Date(r.startedAt);
                const end = r.completedAt ? new Date(r.completedAt) : null;
                const dur = end
                  ? `${Math.round((end.getTime() - start.getTime()) / 1000)}s`
                  : "—";
                return `
                  <tr>
                    <td class="mono">${escapeHtml(fmtDateTime(r.startedAt))}</td>
                    <td class="mono">${dur}</td>
                    <td><span class="pill ${statusPillClass(r.status)}">${escapeHtml(r.status)}</span></td>
                    <td class="mono">${r.articlesSeen ?? "—"}</td>
                    <td class="mono">${r.signalsFound ?? "—"}</td>
                    ${skippedCell(r)}
                    <td class="mono">${r.errorsCount ?? "—"}</td>
                  </tr>`;
              })
              .join("")}
          </tbody>
        </table>
      `;
    } catch (_e) {
      historyContainer.innerHTML = "<p class='muted'>Failed to load history.</p>";
    }
  }

  runBtn.addEventListener("click", async () => {
    // Branch on the state the server reported, not on the button label — reading
    // behaviour out of DOM text meant relabelling the button changed what it did.
    if (_runState === "stopping") return;

    if (_runState === "running") {
      runBtn.disabled = true;
      runBtn.textContent = "Stopping…";
      try {
        await api("/collect/stop", { method: "POST" });
      } catch (err) {
        // 409 now means nothing is running anywhere — the run finished between
        // the click and the request. The status refresh below sorts it out.
        if (!(err instanceof ApiError && err.status === 409)) {
          appendLog(
            `[error] Stop failed: ${err instanceof Error ? err.message : String(err)}`
          );
        }
      }
      await loadStatus();
      void loadHistory();
      return;
    }

    try {
      _streamGaveUp = false;
      _logLineCount = 0;
      _currentRunId = null;
      logOutput.textContent = "";
      const res = await api<RunResponse>("/collect/run", { method: "POST" });
      if (!res.ok) {
        await loadStatus();
        return;
      }
      _currentRunId = res.runId;
      applyState({ status: "running", runId: res.runId, stopRequested: false });
      attachStream();
    } catch (err) {
      const msg =
        err instanceof ApiError && err.status === 409
          ? "Collection already running"
          : err instanceof Error
          ? err.message
          : "Failed to start collection";
      appendLog(`[error] ${msg}`);
      await loadStatus();
    }
  });

  void loadStatus();
  void loadHistory();

  // -------------------------------------------------------------------------
  // Configuration tab
  // -------------------------------------------------------------------------

  const configEditor = shell.querySelector<HTMLTextAreaElement>("#config-editor")!;
  const skillEditor = shell.querySelector<HTMLTextAreaElement>("#skill-editor")!;
  const legacySkill = shell.querySelector<HTMLDetailsElement>("#legacy-skill")!;
  const configSaveMsg = shell.querySelector<HTMLSpanElement>("#config-save-msg")!;
  const guidanceSaveMsg = shell.querySelector<HTMLSpanElement>("#guidance-save-msg")!;
  const guidanceTabs = shell.querySelector<HTMLDivElement>("#guidance-tabs")!;
  const guidanceBlurb = shell.querySelector<HTMLParagraphElement>("#guidance-blurb")!;
  const guidanceEditor = shell.querySelector<HTMLTextAreaElement>("#guidance-editor")!;
  const guidanceCount = shell.querySelector<HTMLParagraphElement>("#guidance-count")!;
  const guidanceBudget = shell.querySelector<HTMLDivElement>("#guidance-budget")!;
  const guidanceLoad = shell.querySelector<HTMLDetailsElement>("#guidance-load")!;
  const glBody = shell.querySelector<HTMLDivElement>("#gl-body")!;

  // Read when the panel is first opened, not with the tab. It is a diff of five
  // documents, and nobody opening the editor to fix a typo has asked for it.
  guidanceLoad.addEventListener("toggle", () => {
    if (guidanceLoad.open) void renderGuidanceLoad();
  });

  interface GuidanceDoc {
    slug: string;
    title: string;
    blurb: string;
    passes: string[];
    content: string;
    max_chars: number;
  }

  /** What the guidance costs one role on every call it makes.
   *
   * The character count above answers "am I near the truncation limit", which
   * is not the limit that bites on a metered free tier. This answers the one
   * that does: these documents are prepended to every call, so their share of
   * a per-minute token budget is the share that is not available for the
   * article or the signals. */
  interface GuidanceBudgetRow {
    role: string;
    label: string;
    model: string | null;
    /** Which of the four sources decided this role's model, in words. */
    source: string;
    documents: string[];
    chars: number;
    tokens: number;
    tpm: number;
    /** null when the provider is unmetered or unresolvable — render it as
     *  "not metered", never as a reassuring 0%. */
    share: number | null;
  }

  const PASS_LABELS: Record<string, string> = {
    pass1: "relevance filter",
    pass2: "signal analysis",
    brief: "brief",
  };

  let configLoaded = false;
  let guidanceDocs: GuidanceDoc[] = [];
  let activeDoc = 0;
  // Edits are held per document rather than written straight back to the loaded
  // copy, so switching tabs never silently discards typing.
  const drafts = new Map<string, string>();

  function currentDoc(): GuidanceDoc | undefined {
    return guidanceDocs[activeDoc];
  }

  interface GuidanceDiff {
    slug: string;
    title: string;
    blurb: string;
    passes: string[];
    /** Nothing to load. True of everything on an install that has never saved
     *  a document, because the app is already reading the shipped file. */
    identical: boolean;
    /** Somebody saved this through the editor. Loading discards their work. */
    edited: boolean;
    currentChars: number;
    shippedChars: number;
    added: number;
    removed: number;
    diff: string;
    diffTruncated: boolean;
  }

  interface GuidanceLoadResult {
    slug: string;
    status: string;
    charsBefore: number;
    charsAfter: number;
  }

  /** Render the diff for every document, with a tick box on the ones that would
   *  actually change. Opened on demand rather than with the tab: it is a
   *  five-document diff, and nobody opening the editor to fix a typo wants it. */
  async function renderGuidanceLoad(): Promise<void> {
    glBody.textContent = "Reading…";
    let diffs: GuidanceDiff[];
    try {
      diffs = await api<GuidanceDiff[]>("/collect/guidance/v6-preview");
    } catch (err) {
      glBody.textContent =
        err instanceof ApiError ? err.message : "Could not read the shipped guidance";
      return;
    }

    const changed = diffs.filter((d) => !d.identical);
    if (!changed.length) {
      glBody.replaceChildren(
        Object.assign(document.createElement("p"), {
          className: "muted",
          textContent:
            "Every document already matches what this build ships. Nothing to load.",
        }),
      );
      return;
    }

    const frag = document.createDocumentFragment();
    for (const d of changed) {
      const row = document.createElement("div");
      row.className = "gl-row";
      row.innerHTML = `
        <label class="gl-head">
          <input type="checkbox" class="gl-check" data-slug="${escapeHtml(d.slug)}" checked />
          <strong>${escapeHtml(d.title)}</strong>
          <span class="gl-counts">+${d.added} / −${d.removed}</span>
          ${d.edited
            ? '<span class="gl-warn">edited here — loading discards that</span>'
            : ""}
        </label>
        <details class="gl-diff">
          <summary>Show the diff</summary>
          <pre>${escapeHtml(d.diff)}${d.diffTruncated ? "\n… diff truncated" : ""}</pre>
        </details>`;
      frag.appendChild(row);
    }

    const actions = document.createElement("div");
    actions.className = "gl-actions";
    actions.innerHTML = `
      <span class="save-msg" id="gl-msg"></span>
      <button class="primary" id="gl-apply">Load the ticked documents</button>`;
    frag.appendChild(actions);

    glBody.replaceChildren(frag);

    glBody.querySelector<HTMLButtonElement>("#gl-apply")!.addEventListener(
      "click",
      async () => {
        const slugs = Array.from(
          glBody.querySelectorAll<HTMLInputElement>(".gl-check:checked"),
        ).map((el) => el.dataset.slug!);
        const msg = glBody.querySelector<HTMLSpanElement>("#gl-msg")!;
        if (!slugs.length) {
          showSaveMsg(msg, false, "Nothing ticked");
          return;
        }
        if (
          !confirm(
            `Replace ${slugs.length} guidance document${slugs.length > 1 ? "s" : ""} ` +
              `with the text this build ships? This overwrites what is stored.`,
          )
        ) {
          return;
        }
        try {
          const results = await api<GuidanceLoadResult[]>("/collect/guidance/v6-load", {
            method: "POST",
            body: { slugs },
          });
          const loaded = results.filter((r) => r.status === "loaded").length;
          // The editor is now holding stale text, so re-read rather than patch:
          // the server is what decides what each document contains.
          configLoaded = false;
          drafts.clear();
          await loadConfigEditors();
          await renderGuidanceLoad();
          showSaveMsg(
            glBody.querySelector<HTMLSpanElement>("#gl-msg") ?? msg,
            true,
            `Loaded ${loaded} document${loaded === 1 ? "" : "s"}`,
          );
        } catch (err) {
          showSaveMsg(msg, false, err instanceof ApiError ? err.message : "Load failed");
        }
      },
    );
  }

  function renderGuidanceBudget(rows: GuidanceBudgetRow[]): void {
    guidanceBudget.hidden = !rows.length;
    if (!rows.length) return;
    guidanceBudget.replaceChildren(
      Object.assign(document.createElement("p"), {
        className: "muted",
        textContent:
          "What these documents cost on every call, before the article or the signals:",
      }),
      ...rows.map((row) => {
        const line = document.createElement("p");
        // Three bands, and the top one is not a limit — nothing is refused at
        // 40%. It is the point at which the documents have started deciding how
        // many signals fit in a brief, which is worth knowing before the brief
        // stops arriving rather than after.
        const pct = row.share === null ? null : Math.round(row.share * 100);
        line.className =
          pct === null ? "budget-row" : pct >= 70 ? "budget-row over-limit"
          : pct >= 40 ? "budget-row near-limit" : "budget-row";
        // The source in parentheses because it answers a different question
        // from the cost, and an operator reading this panel to decide what to
        // trim should not have to open another screen to learn that the model
        // it names was set somewhere they were not looking.
        const on = row.model
          ? ` on ${row.model}${row.source ? ` (${row.source})` : ""}`
          : "";
        line.textContent =
          pct === null
            ? `${row.label}${on}: ~${row.tokens.toLocaleString()} tokens — not metered`
            : `${row.label}${on}: ~${row.tokens.toLocaleString()} tokens, ` +
              `${pct}% of a ${row.tpm.toLocaleString()}/min budget`;
        return line;
      }),
    );
  }

  function renderGuidanceCount(): void {
    const doc = currentDoc();
    const used = guidanceEditor.value.length;
    if (!doc) {
      guidanceCount.textContent = "";
      return;
    }
    const max = doc.max_chars;
    const over = used > max;
    // 90% rather than "when it breaks": the whole point is to be told before
    // the end of the document starts being ignored, not after.
    const near = !over && used > max * 0.9;
    guidanceCount.textContent = over
      ? `${used.toLocaleString()} / ${max.toLocaleString()} characters — everything past ` +
        `${max.toLocaleString()} is ignored on every article`
      : `${used.toLocaleString()} / ${max.toLocaleString()} characters`;
    guidanceCount.classList.toggle("over-limit", over);
    guidanceCount.classList.toggle("near-limit", near);
  }

  function showDoc(index: number): void {
    const doc = guidanceDocs[index];
    if (!doc) return;
    activeDoc = index;
    guidanceEditor.value = drafts.get(doc.slug) ?? doc.content;
    // Who reads this document, said plainly. Splitting one document into four
    // only helps if the person editing can tell which one they are in.
    const audience = doc.passes.map((p) => PASS_LABELS[p] ?? p).join(", ");
    guidanceBlurb.textContent = `${doc.blurb} — read by: ${audience}.`;
    guidanceTabs.querySelectorAll<HTMLButtonElement>("button").forEach((btn, i) => {
      const on = i === index;
      btn.classList.toggle("active", on);
      btn.setAttribute("aria-selected", String(on));
      // A dot marks a document with unsaved edits, so an unsaved tab is
      // visible without opening it.
      const slug = guidanceDocs[i]?.slug;
      btn.classList.toggle("dirty", slug !== undefined && drafts.has(slug));
    });
    renderGuidanceCount();
  }

  function renderGuidanceTabs(): void {
    guidanceTabs.replaceChildren(
      ...guidanceDocs.map((doc, i) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "doc-tab";
        btn.setAttribute("role", "tab");
        btn.textContent = doc.title;
        btn.addEventListener("click", () => showDoc(i));
        return btn;
      }),
    );
  }

  guidanceEditor.addEventListener("input", () => {
    const doc = currentDoc();
    if (doc) {
      if (guidanceEditor.value === doc.content) {
        drafts.delete(doc.slug);
      } else {
        drafts.set(doc.slug, guidanceEditor.value);
      }
      guidanceTabs.querySelectorAll<HTMLButtonElement>("button")[activeDoc]
        ?.classList.toggle("dirty", drafts.has(doc.slug));
    }
    renderGuidanceCount();
  });

  async function loadConfigEditors(): Promise<void> {
    if (configLoaded) return;
    configLoaded = true;
    try {
      const [cfg, docs, skill, budget] = await Promise.all([
        api<{ content: string }>("/collect/config"),
        api<GuidanceDoc[]>("/collect/guidance"),
        api<{ content: string; max_chars: number | null }>("/collect/skill"),
        // Its own failure, caught here: a budget the server cannot compute is
        // a missing diagnostic, not a reason the editor should refuse to open.
        api<GuidanceBudgetRow[]>("/collect/guidance/budget").catch(() => []),
      ]);
      configEditor.value = cfg.content;
      guidanceDocs = docs;
      renderGuidanceBudget(budget);
      renderGuidanceTabs();
      showDoc(0);
      skillEditor.value = skill.content;
      // Only worth showing at all if there is something in it to move across.
      legacySkill.hidden = !skill.content.trim();
    } catch (_e) {
      configSaveMsg.textContent = "Failed to load configuration";
    }
  }

  function showSaveMsg(el: HTMLSpanElement, ok: boolean, text: string): void {
    el.textContent = text;
    el.className = `save-msg ${ok ? "save-ok" : "save-err"}`;
    setTimeout(() => {
      el.textContent = "";
      el.className = "save-msg";
    }, 3000);
  }

  shell.querySelector<HTMLButtonElement>("#save-config-btn")!.addEventListener("click", async () => {
    try {
      await api("/collect/config", { method: "POST", body: { content: configEditor.value } });
      showSaveMsg(configSaveMsg, true, "Saved");
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Save failed";
      showSaveMsg(configSaveMsg, false, msg);
    }
  });

  shell.querySelector<HTMLButtonElement>("#save-guidance-btn")!.addEventListener("click", async () => {
    // Saves every edited document, not just the visible one. Several tabs
    // behind one Save button otherwise means an edit two tabs back is lost to a
    // navigation nobody thought of as destructive.
    const pending = guidanceDocs.filter((d) => drafts.has(d.slug));
    if (!pending.length) {
      showSaveMsg(guidanceSaveMsg, true, "No changes");
      return;
    }
    try {
      for (const doc of pending) {
        await api(`/collect/guidance/${doc.slug}`, {
          method: "POST",
          body: { content: drafts.get(doc.slug) ?? "" },
        });
        doc.content = drafts.get(doc.slug) ?? "";
        drafts.delete(doc.slug);
      }
      showDoc(activeDoc);
      // The saved text is the input to these numbers, so they are stale the
      // moment the save lands. Re-read rather than recompute in the browser:
      // the share depends on which model each role resolves to, which only the
      // server knows.
      renderGuidanceBudget(
        await api<GuidanceBudgetRow[]>("/collect/guidance/budget").catch(() => []),
      );
      showSaveMsg(
        guidanceSaveMsg,
        true,
        pending.length === 1 ? "Saved" : `Saved ${pending.length} documents`,
      );
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Save failed";
      showSaveMsg(guidanceSaveMsg, false, msg);
    }
  });

  // -------------------------------------------------------------------------
  // Submit URLs tab
  // -------------------------------------------------------------------------

  const urlInput = shell.querySelector<HTMLTextAreaElement>("#url-input")!;
  const submitUrlsBtn = shell.querySelector<HTMLButtonElement>("#submit-urls-btn")!;
  const urlSubmitStatus = shell.querySelector<HTMLSpanElement>("#url-submit-status")!;
  const urlResults = shell.querySelector<HTMLDivElement>("#url-results")!;

  /** How each outcome is drawn. Three shapes, not two: a duplicate is neutral.
   *
   * Marking "already collected" with a red ✗ beside a stringified exception was
   * the single most misleading thing this page did — the app had worked exactly
   * as intended, and the analyst was being told their URL had failed. */
  const URL_STATE: Record<string, { cls: string; icon: string }> = {
    success: { cls: "url-ok", icon: "✓" },
    duplicate: { cls: "url-dup", icon: "↺" },
    deferred: { cls: "url-wait", icon: "⏱" },
    stopped: { cls: "url-wait", icon: "⏸" },
  };
  const URL_FAIL = { cls: "url-fail", icon: "✗" };

  function urlResultRow(r: UrlResult): string {
    const { cls, icon } = URL_STATE[r.status] ?? URL_FAIL;

    let detail: string;
    if (r.status === "success") {
      detail = `<span class="muted">Saved as signal ${escapeHtml(r.signal_id?.slice(0, 8) ?? "")}…</span>`;
    } else if (r.status === "duplicate") {
      // initRouter() delegates every a[data-route] click, so an in-app link
      // needs nothing but the attribute; folder_view reads ?signal= on load.
      detail = r.signal_id
        ? `<a class="url-dup-link" data-route href="/folders?signal=${encodeURIComponent(r.signal_id)}">Already collected — open signal</a>`
        : `<span class="muted">Already collected.</span>`;
    } else {
      detail = `<span class="muted">${escapeHtml(r.reason ?? "Failed")}</span>`;
    }

    return `
      <div class="url-result ${cls}">
        <span class="url-result-status">${icon}</span>
        <div class="url-result-detail">
          <span class="mono url-result-url">${escapeHtml(r.url)}</span>
          ${detail}
        </div>
      </div>
    `;
  }

  submitUrlsBtn.addEventListener("click", async () => {
    const all = urlInput.value
      .split("\n")
      .map((u) => u.trim())
      .filter(Boolean);
    if (!all.length) return;

    // Sliced here as well as capped on the server. The 422 is the honest answer
    // to a client that ignores the limit; it is a poor answer to an analyst who
    // pasted twelve links, because it stores none of them. Take the first ten,
    // say so, and leave the rest in the box to submit next.
    const urls = all.slice(0, MAX_URLS);
    const overflow = all.length - urls.length;

    submitUrlsBtn.disabled = true;
    urlSubmitStatus.textContent = `Analysing ${urls.length} URL(s)…`;
    urlResults.innerHTML = "";

    try {
      const res = await api<UrlSubmitResponse>("/collect/urls", {
        method: "POST",
        body: { urls },
        // Above the server's own 240-second batch deadline, so this fires only
        // when the request is genuinely lost rather than merely slow.
        timeoutMs: 270_000,
      });

      // The batch answers 200 whatever happened to the individual URLs, so the
      // modal is raised from the body here rather than by api()'s 429 handling.
      // The successes above it are already stored and stay on screen.
      if (res.rateLimit) showRateLimitModal(res.rateLimit);

      const notes: string[] = [];
      if (overflow) {
        notes.push(
          `${overflow} URL(s) beyond the limit of ${MAX_URLS} were not submitted — they are still in the box.`
        );
      }
      if (res.truncated) {
        notes.push("The batch ended early; the URLs marked below were never attempted.");
      }
      urlSubmitStatus.textContent = notes.join(" ");

      urlResults.innerHTML = res.results.map(urlResultRow).join("");

      // Keep only what was not submitted, so the next click is the obvious one.
      urlInput.value = all.slice(urls.length).join("\n");
    } catch (err) {
      urlSubmitStatus.textContent = err instanceof Error ? err.message : "Submission failed";
    } finally {
      submitUrlsBtn.disabled = false;
    }
  });

  // -------------------------------------------------------------------------
  // Automated Scanning tab (admin only)
  // -------------------------------------------------------------------------

  async function loadScanning(): Promise<void> {
    const body = shell.querySelector<HTMLDivElement>("#scan-body");
    if (!body) return;
    body.innerHTML = `<p class="muted">Loading…</p>`;
    try {
      await ensureAppTimezone();
      renderScanning(body, await api<ScanSchedule>("/collect/scanning"));
    } catch (err) {
      body.innerHTML = `<p class="muted">${escapeHtml(
        err instanceof ApiError ? err.message : "Could not load scanning settings."
      )}</p>`;
    }
  }

  function renderScanning(body: HTMLDivElement, s: ScanSchedule): void {
    const dayLabels: Record<string, string> = {
      mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun",
    };
    const hhmm = `${String(s.hour).padStart(2, "0")}:${String(s.minute).padStart(2, "0")}`;
    const briefHhmm = `${String(s.briefHour).padStart(2, "0")}:${String(s.briefMinute).padStart(2, "0")}`;
    const tzOptions = TIMEZONES.includes(s.timezone) ? TIMEZONES : [s.timezone, ...TIMEZONES];
    // The brief summarises the scan, so a brief before the scan summarises
    // yesterday. Worth saying out loud rather than leaving to be discovered.
    const briefBeforeScan =
      s.briefHour * 60 + s.briefMinute <= s.hour * 60 + s.minute;

    body.innerHTML = `
      <div class="scan-status ${s.enabled ? "scan-on" : "scan-off"}">
        <span class="scan-status-dot"></span>
        <span>${s.enabled ? "Scheduled scanning is ON" : "Scheduled scanning is OFF"}</span>
      </div>

      <div class="scan-grid">
        <label class="scan-toggle">
          <input type="checkbox" id="scan-enabled" ${s.enabled ? "checked" : ""} />
          <span>Enable automated scanning</span>
        </label>

        <div class="scan-field">
          <span class="scan-label">Days</span>
          <div class="scan-days">
            ${s.allDays.map((d) => `
              <label class="scan-day">
                <input type="checkbox" class="scan-day-cb" value="${d}" ${s.days.includes(d) ? "checked" : ""} />
                <span>${dayLabels[d] ?? d}</span>
              </label>`).join("")}
          </div>
        </div>

        <div class="scan-field">
          <span class="scan-label">Time</span>
          <div class="scan-time-row">
            <input type="time" id="scan-time" class="ap-input scan-time" value="${hhmm}" />
            <select id="scan-tz" class="ap-select scan-tz">
              ${tzOptions.map((t) => `<option value="${escapeHtml(t)}"${t === s.timezone ? " selected" : ""}>${escapeHtml(t)}</option>`).join("")}
            </select>
          </div>
        </div>

        <div class="scan-field">
          <span class="scan-label">Morning brief</span>
          <div>
            <div class="scan-time-row">
              <input type="time" id="scan-brief-time" class="ap-input scan-time" value="${briefHhmm}" />
              <label class="scan-toggle scan-toggle-inline">
                <input type="checkbox" id="scan-brief-enabled" ${s.briefEnabled ? "checked" : ""} />
                <span>Send the brief</span>
              </label>
            </div>
            <p class="muted scan-hint">
              Sent on the same days and in the same time zone as the scan${
                s.briefEnabled && briefBeforeScan
                  ? ` — but this time is before the scan, so each brief covers the previous day's signals`
                  : ""
              }. A day with no signals gets no brief.
            </p>
          </div>
        </div>

        <div class="scan-field">
          <span class="scan-label">Max articles per run</span>
          <div>
            <input type="number" id="scan-cap" class="ap-input scan-cap" min="0" step="1" value="${s.maxArticlesPerRun}" />
            <p class="muted scan-hint">0 = no limit. A cap protects against a runaway feed day and against exhausting a rate-limited free tier in one run.</p>
          </div>
        </div>
      </div>

      <div class="scan-actions">
        <button class="primary" id="scan-save">Save</button>
        <span class="muted" id="scan-save-status"></span>
      </div>

      <div class="scan-info">
        <div class="scan-info-row">
          <span class="scan-info-label">Next run</span>
          <span class="scan-info-value">${s.nextRunAt ? escapeHtml(fmtSchedule(s.nextRunAt)) : "—"}</span>
        </div>
        <div class="scan-info-row">
          <span class="scan-info-label">Next brief</span>
          <span class="scan-info-value">${
            s.nextBriefAt
              ? escapeHtml(fmtSchedule(s.nextBriefAt))
              : escapeHtml(noBriefReason(s))
          }</span>
        </div>
        <div class="scan-info-row">
          <span class="scan-info-label">Last run</span>
          <span class="scan-info-value">${s.lastRun ? escapeHtml(fmtLastRun(s.lastRun)) : "Never"}</span>
        </div>
        <div class="scan-info-row">
          <span class="scan-info-label">Last brief</span>
          <span class="scan-info-value">${s.lastBrief ? escapeHtml(fmtLastBrief(s.lastBrief)) : "Never"}</span>
        </div>
        ${fmtBriefAttemptsRow(s.briefAttempts?.daily ?? null, "Daily attempts")}
        ${fmtBriefAttemptsRow(s.briefAttempts?.weekly ?? null, "Weekly attempts")}
        ${fmtBriefCostRow(s.briefCost ?? null)}
      </div>
    `;

    body.querySelector<HTMLButtonElement>("#scan-save")!.addEventListener("click", async (e) => {
      const btn = e.currentTarget as HTMLButtonElement;
      const status = body.querySelector<HTMLElement>("#scan-save-status")!;
      const days = Array.from(body.querySelectorAll<HTMLInputElement>(".scan-day-cb:checked"))
        .map((cb) => cb.value);
      if (days.length === 0) {
        status.textContent = "Select at least one day.";
        return;
      }
      const [h, m] = body.querySelector<HTMLInputElement>("#scan-time")!.value.split(":");
      const [bh, bm] = body.querySelector<HTMLInputElement>("#scan-brief-time")!.value.split(":");
      btn.disabled = true;
      status.textContent = "Saving…";
      try {
        const updated = await api<ScanSchedule>("/collect/scanning", {
          method: "PUT",
          body: {
            enabled: body.querySelector<HTMLInputElement>("#scan-enabled")!.checked,
            days,
            hour: Number(h ?? 0),
            minute: Number(m ?? 0),
            timezone: body.querySelector<HTMLSelectElement>("#scan-tz")!.value,
            maxArticlesPerRun: Number(body.querySelector<HTMLInputElement>("#scan-cap")!.value || 0),
            briefEnabled: body.querySelector<HTMLInputElement>("#scan-brief-enabled")!.checked,
            briefHour: Number(bh ?? 0),
            briefMinute: Number(bm ?? 0),
          },
        });
        // This is the one save that can change the app time zone, and every
        // date on every page is rendered in it. Drop the cache before painting
        // the panel, or "Next brief" comes back in the previous zone.
        await ensureAppTimezone(true);
        renderScanning(body, updated);
        body.querySelector<HTMLElement>("#scan-save-status")!.textContent = "Saved.";
      } catch (err) {
        status.textContent = err instanceof ApiError ? err.message : "Save failed.";
      } finally {
        btn.disabled = false;
      }
    });
  }
}

/** Common IANA zones, with the project's home zone first. */
const TIMEZONES = [
  "Asia/Singapore", "UTC", "Asia/Tokyo", "Asia/Shanghai", "Asia/Kolkata",
  "Asia/Dubai", "Europe/London", "Europe/Brussels", "America/New_York",
  "America/Los_Angeles", "Australia/Sydney",
];

/** Why there is no next brief, in the same place the time would have been.
 *  An empty cell here is the bug this stage exists to fix: it reads as "the
 *  brief is fine" whether the brief is off, unscheduled, or broken. */
function noBriefReason(s: ScanSchedule): string {
  if (!s.enabled) return "Not scheduled — automated scanning is off";
  if (s.days.length === 0) return "Not scheduled — no days selected";
  if (!s.briefEnabled) return "Not scheduled — the brief is switched off";
  return "—";
}

function fmtLastBrief(b: LastBrief): string {
  const when = fmtDateTime(b.at);
  if (b.status === "skipped_no_signals") {
    return `Skipped for ${b.date} — no signals to report on (${when})`;
  }
  if (b.status === "failed") {
    return `Failed for ${b.date} — ${b.detail ?? "see the logs"} (${when})`;
  }
  // Before the fallback, which says "Sent": an unhandled status here would
  // report a brief as delivered when it has not been written yet.
  if (b.status === "generating") {
    return `Still generating for ${b.date} — ${b.detail ?? "taking longer than usual"} (${when})`;
  }
  return `Sent for ${b.date}${b.detail ? ` — ${b.detail}` : ""} (${when})`;
}

/** A row only when the last attempt log has something worth reading: a day
 *  that needed more than one try, or ended on a failure. One clean attempt is
 *  the normal morning and would be noise. */
function fmtBriefAttemptsRow(log: BriefAttempts | null, label: string): string {
  const last = log?.attempts[log.attempts.length - 1];
  if (!log || !last) return "";
  if (log.attempts.length === 1 && last.outcome === "ok") return "";
  const tries = log.attempts
    .map((a) => `${fmtDateTime(a.at)} ${a.outcome === "ok" ? "ok" : a.outcome.replace("_", " ")}`)
    .join(" · ");
  const detail = last.outcome === "ok" ? "" : ` — ${last.detail || "see the logs"}`;
  return `
        <div class="scan-info-row">
          <span class="scan-info-label">${escapeHtml(label)}</span>
          <span class="scan-info-value">${escapeHtml(
            `${log.day}: ${log.attempts.length} of ${log.maxAttempts} — ${tries}${detail}`,
          )}</span>
        </div>`;
}

const COST_MODEL_LABELS: Record<string, string> = {
  "claude-haiku-4-5": "Haiku 4.5",
  "claude-sonnet-5-5": "Sonnet 5.5",
};

/** The free-tier decision's missing number: what these briefs would cost on
 *  Claude. An estimate — the tokens are Gemma's — so it says so. */
function fmtBriefCostRow(cost: ScanSchedule["briefCost"] | null): string {
  if (!cost) return "";
  const prices = Object.entries(cost.perMonthUsd)
    .map(([id, usd]) => `${COST_MODEL_LABELS[id] ?? id} ≈ $${usd.toFixed(2)}`)
    .join(", ");
  const n = Object.values(cost.samples).reduce((a, b) => a + b, 0);
  return `
        <div class="scan-info-row">
          <span class="scan-info-label">Briefs on Claude</span>
          <span class="scan-info-value">${escapeHtml(
            `${prices} a month — estimated from ${n} measured brief(s)`,
          )}</span>
        </div>`;
}

function fmtLastRun(r: RunSummary): string {
  // The app zone, not the viewer's: the server files signals by it, so a run
  // stamped in a second zone would put "last run" on a different day from the
  // signals it produced.
  const stamp = fmtDateTime(r.startedAt);
  const bits = [`Last run: ${stamp} — ${r.status}`];
  if (r.signalsFound != null) bits.push(`${r.signalsFound} signal(s)`);
  // Named here too, not just in the table: a run that stored nothing is the one
  // an analyst is most likely to be looking at, and "0 signals" on its own
  // invites the wrong conclusion about which part is broken.
  const skipped =
    (r.skippedRelevance ?? 0) + (r.skippedParse ?? 0) + (r.skippedLlm ?? 0);
  if (skipped) bits.push(`${skipped} skipped`);
  if (r.errorsCount) bits.push(`${r.errorsCount} error(s)`);
  return bits.join(", ");
}

/** `stopped` is neutral — an analyst ended it, which is not a failure.
 *  `interrupted` is a warning: the run never reached a conclusion because the
 *  process went away, so its counts are a floor rather than a result. */
function statusPillClass(status: string): string {
  switch (status) {
    case "completed":
      return "status-ok";
    case "running":
      return "status-running";
    case "stopped":
      return "status-neutral";
    case "interrupted":
      return "status-warn";
    default:
      return "status-err";
  }
}

/** Total skips, with the breakdown on hover. One number keeps the table
 *  readable; the three causes are what actually tell you whether to change
 *  anything, so they are one hover away rather than absent. */
function skippedCell(r: RunSummary): string {
  if (
    r.skippedRelevance == null &&
    r.skippedParse == null &&
    r.skippedLlm == null
  ) {
    return `<td class="mono">—</td>`;
  }
  const rel = r.skippedRelevance ?? 0;
  const parse = r.skippedParse ?? 0;
  const llm = r.skippedLlm ?? 0;
  const title = `${rel} not relevant, ${parse} unparseable reply, ${llm} LLM unavailable`;
  return `<td class="mono" title="${escapeHtml(title)}">${rel + parse + llm}</td>`;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
